import { Capacitor, registerPlugin } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { parseExternalAppReturnLink } from './externalAuthReturn';
import { apiRequest, getDeviceId } from './appApi';
import type { VerifyOtpResult } from '@/contexts/AuthContext';

export type ExternalProvider = 'x' | 'telegram' | 'apple';
export type ExternalIntent = 'login' | 'bind' | 'unlink';
export interface ExternalBinding {
  bound: boolean;
  email_masked: string | null;
  bound_at: number | null;
  provider_revocation?: 'pending' | 'completed' | null;
}
export type ExternalStep =
  | { status: 'awaiting_provider' | 'needs_email_input' | 'needs_account_choice' | 'canceled' | 'failed' | 'already_completed' }
  | { status: 'needs_terms'; terms_version: string }
  | { status: 'needs_email'; email_masked: string; rate_seconds: number }
  | { status: 'needs_totp'; remaining_attempts: number }
  | { status: 'authenticated'; auth: VerifyOtpResult }
  | { status: 'bound' | 'unbound' | 'revocation_pending'; binding: ExternalBinding; provider_revocation?: string };

interface StartResult {
  attempt_token: string;
  expires_in: number;
  client_id: string;
  nonce: string | null;
  state: string;
  transport: 'web' | 'native_browser' | 'native_credential';
  authorization_url: string | null;
  step: ExternalStep | null;
}
export interface PendingExternalAuth {
  provider: ExternalProvider;
  intent: ExternalIntent;
  attempt_token: string;
  flow_id: string;
  native_verifier?: string;
  expires_at: number;
}
interface AppleNative {
  signIn(options: { state: string; nonce: string }): Promise<{
    state: string; authorizationCode: string; identityToken: string;
  }>;
}
const appleNative = registerPlugin<AppleNative>('AppleIdentity');
const PENDING_KEY = 'fm_external_auth_pending';
const native = () => Capacitor.isNativePlatform();
const clientKeys: Record<ExternalProvider, { web: string; native: string; ios?: string }> = {
  x: { web: 'forex-x-web', native: 'forex-x-native-browser' },
  telegram: { web: 'forex-telegram-web', native: 'forex-telegram-native-browser' },
  apple: { web: 'forex-apple-web', native: 'forex-apple-native-browser', ios: 'forex-apple-ios-native' },
};

function randomVerifier(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
async function sha256(value: string): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return Array.from(new Uint8Array(hash), byte => byte.toString(16).padStart(2, '0')).join('');
}
async function challenge(verifier: string): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return btoa(String.fromCharCode(...new Uint8Array(hash))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function request<T>(provider: ExternalProvider, path: string, body?: object, accessToken?: string | null): Promise<T> {
  const endpoint = `/app/auth/external/${provider}${path}`;
  return apiRequest<T>(endpoint, {
    method: body ? 'POST' : 'GET',
    body: body ? JSON.stringify(body) : undefined,
    credentials: native() ? 'omit' : 'include',
    cache: 'no-store',
  }, accessToken);
}
function proof(pending: PendingExternalAuth) {
  return { attempt_token: pending.attempt_token, device_id: getDeviceId(),
    native_verifier: pending.native_verifier };
}
export function readPendingExternalAuth(): PendingExternalAuth | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as PendingExternalAuth;
    if (!['x', 'telegram', 'apple'].includes(value.provider) || !['login', 'bind', 'unlink'].includes(value.intent)
      || !/^[a-f0-9]{64}$/.test(value.flow_id) || !/^[A-Za-z0-9_-]{32,128}$/.test(value.attempt_token)
      || typeof value.expires_at !== 'number' || value.expires_at <= Date.now()) {
      sessionStorage.removeItem(PENDING_KEY);
      return null;
    }
    return value;
  } catch {
    sessionStorage.removeItem(PENDING_KEY);
    return null;
  }
}
export function clearPendingExternalAuth() { sessionStorage.removeItem(PENDING_KEY); }

export const routeForExternalAppLink = parseExternalAppReturnLink;

export const externalAuth = {
  async start(provider: ExternalProvider, intent: ExternalIntent, accessToken?: string | null, email?: string) {
    clearPendingExternalAuth();
    const iosApple = provider === 'apple' && native() && Capacitor.getPlatform() === 'ios';
    const transport = iosApple ? 'native_credential' : native() ? 'native_browser' : 'web';
    const verifier = transport === 'native_browser' ? randomVerifier() : undefined;
    const client_key = iosApple ? clientKeys.apple.ios! : native() ? clientKeys[provider].native : clientKeys[provider].web;
    const start = await request<StartResult>(provider, '/start', {
      intent, device_id: getDeviceId(), client_key, email,
      redeem_challenge: verifier ? await challenge(verifier) : undefined,
    }, accessToken);
    if (start.transport !== transport) throw new Error('授权配置与当前设备不匹配');
    const pending: PendingExternalAuth = { provider, intent, attempt_token: start.attempt_token,
      flow_id: await sha256(start.attempt_token), native_verifier: verifier,
      expires_at: Date.now() + Math.min(start.expires_in, 1200) * 1000 };
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
    return { start, pending };
  },
  async openBrowser(start: StartResult) {
    if (!start.authorization_url || !start.authorization_url.startsWith('https://')) throw new Error('授权地址无效');
    if (native()) await Browser.open({ url: start.authorization_url });
    else window.location.assign(start.authorization_url);
  },
  async exchangeApple(pending: PendingExternalAuth, state: string, nonce: string) {
    const credential = await appleNative.signIn({ state, nonce });
    return request<ExternalStep>('apple', '/exchange', { ...proof(pending), state: credential.state,
      authorization_code: credential.authorizationCode, identity_token: credential.identityToken });
  },
  continue(pending: PendingExternalAuth, handoff: string) {
    return request<ExternalStep>(pending.provider, '/continue', { ...proof(pending), handoff });
  },
  selectEmail(pending: PendingExternalAuth, email: string) {
    return request<ExternalStep>(pending.provider, '/select-email', { ...proof(pending), email });
  },
  selectAccount(pending: PendingExternalAuth, choice: 'new' | 'existing') {
    return request<ExternalStep>('apple', '/select-account', { ...proof(pending), choice });
  },
  sendEmail(pending: PendingExternalAuth) {
    return request<{ rate_seconds: number }>(pending.provider, '/email-code', proof(pending));
  },
  complete(pending: PendingExternalAuth, fields: { agreed_terms?: boolean; terms_version?: string; otp?: string; totp_code?: string }) {
    return request<ExternalStep>(pending.provider, '/complete', { ...proof(pending), ...fields });
  },
  cancel(pending: PendingExternalAuth) {
    return request<ExternalStep>(pending.provider, '/cancel', proof(pending));
  },
  binding(provider: ExternalProvider, accessToken: string) {
    return request<ExternalBinding>(provider, '/binding', undefined, accessToken);
  },
  closeBrowser() { if (native()) return Browser.close().catch(() => undefined); return Promise.resolve(); },
};
